import type { CartItem } from '@/types/ecommerce'
import { checkCartAvailability, removeFromCart } from './api'

/* Race-safe one-of-a-kind enforcement for the MIG checkout flow.
   Whenever any checkout page (or the cart) loads, it asks the
   /api/cart/check-availability endpoint whether anything in the
   buyer's cart has been marked Sold by another buyer's checkout.
   If so:

   1. The affected cart_item rows are deleted via the addon's
      remove-from-cart endpoint (so the cart total updates and the
      next checkout pass cannot see them).
   2. The names of the evicted artworks are stashed in
      sessionStorage so /cart can show a toast as soon as the buyer
      lands there.
   3. The caller is redirected to /cart unless they're already
      there — at which point the toast lives on the cart page
      itself and triggers via the same sessionStorage handshake.

   The helper is idempotent and silently no-ops when the cart is
   empty or the network call fails (the payment-time wrapper in
   CheckoutPayment is the last-line gate). */

const STORAGE_KEY = 'mig-sold-evict'

export type EvictionReason = 'sold' | 'disabled'

export interface SoldEvictionPayload {
  names: string[]
  /** Per-name eviction reason (parallel-indexed to `names`). Used by the
   *  /cart toast to render the right copy ("sold to another buyer" vs
   *  "no longer available"). Optional for backwards-compat with older
   *  payloads that pre-date this field. */
  reasons?: EvictionReason[]
  at: number
}

export function readSoldEvictionToast(): SoldEvictionPayload | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SoldEvictionPayload
    if (!parsed?.names?.length) return null
    return parsed
  } catch {
    return null
  }
}

export function clearSoldEvictionToast(): void {
  if (typeof window === 'undefined') return
  try { window.sessionStorage.removeItem(STORAGE_KEY) } catch { /* noop */ }
}

interface NotyfInstance {
  error: (message: string) => void
  success: (message: string) => void
}
interface NotyfConstructor {
  new (options: {
    duration?: number
    position?: { x: 'left' | 'center' | 'right'; y: 'top' | 'center' | 'bottom' }
    dismissible?: boolean
  }): NotyfInstance
}

/* Render the upper-right "no longer available" notification using
   the same Notyf instance the addon's checkout uses for its
   "Credit card has been added" success toast. The library is
   loaded site-wide by vendor-assets.liquid, so it's safe to use
   anywhere in the React app. Quietly degrades to a console log
   if Notyf is not loaded (development with the CDN blocked, etc.).

   When mixed reasons are present (one artwork sold + one disabled),
   we render two toasts so each carries the correct copy. The simpler
   single-reason path renders one combined toast. */
export function showSoldEvictionToast(
  names: string[],
  reasons?: EvictionReason[],
): void {
  if (typeof window === 'undefined') return
  if (names.length === 0) return

  // Group names by reason so each reason gets its own toast with the
  // right copy. Legacy callers that pass only `names` (no reasons)
  // default the whole batch to "sold" — that was the only reason
  // before this change shipped.
  const buckets: Record<EvictionReason, string[]> = { sold: [], disabled: [] }
  names.forEach((n, i) => {
    const r: EvictionReason = reasons?.[i] === 'disabled' ? 'disabled' : 'sold'
    buckets[r].push(n)
  })

  const Notyf = (window as unknown as { Notyf?: NotyfConstructor }).Notyf
  const fire = (msg: string) => {
    if (!Notyf) {
      // eslint-disable-next-line no-console
      console.warn('[mig-availability] Notyf not loaded; falling back to log:', msg)
      return
    }
    new Notyf({
      duration: 7000,
      position: { x: 'right', y: 'top' },
      dismissible: true,
    }).error(msg)
  }

  const join = (xs: string[]) =>
    xs.length === 1
      ? xs[0]
      : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`

  if (buckets.sold.length > 0) {
    const subject = join(buckets.sold)
    fire(
      buckets.sold.length === 1
        ? `${subject} has been sold to another buyer and was removed from your cart.`
        : `${subject} have been sold to another buyer and were removed from your cart.`,
    )
  }
  if (buckets.disabled.length > 0) {
    const subject = join(buckets.disabled)
    fire(
      buckets.disabled.length === 1
        ? `${subject} is no longer available and was removed from your cart.`
        : `${subject} are no longer available and were removed from your cart.`,
    )
  }
}

function writeSoldEvictionToast(names: string[], reasons: EvictionReason[]): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ names, reasons, at: Date.now() } satisfies SoldEvictionPayload),
    )
  } catch { /* noop */ }
}

export interface EvictResult {
  /** Number of cart_items deleted. 0 means nothing was evicted. */
  removed: number
  /** Display names of removed artworks, in cart order. */
  names: string[]
  /** Reason per evicted item — parallel-indexed to `names`. */
  reasons: EvictionReason[]
}

/* Check the cart and, if anything is unavailable (Sold OR Disabled in
   IIA), delete those cart_items and (when not already on /cart)
   redirect there. Returns whether any items were evicted so the caller
   can short-circuit further work — checkout pages should NOT continue
   to render their UI when this returns true. */
export async function evictSoldOrRedirect(opts: {
  items: CartItem[]
  contactUuid: string | null
  /** When true, skip the redirect even if items were evicted (used
   *  by /cart so it can refresh in place and show the toast). */
  stay?: boolean
}): Promise<EvictResult> {
  const { items, contactUuid, stay = false } = opts
  const productUuids = items.map((it) => it.product_uuid).filter(Boolean)
  if (productUuids.length === 0) return { removed: 0, names: [], reasons: [] }

  let availability
  try {
    availability = await checkCartAvailability(productUuids)
  } catch {
    return { removed: 0, names: [], reasons: [] }
  }
  if (availability.ok) return { removed: 0, names: [], reasons: [] }

  // Prefer the new `unavailable` field; fall back to `sold` for
  // backwards-compat with an endpoint that hasn't been redeployed yet.
  const entries = availability.unavailable ?? availability.sold ?? []

  const names: string[] = []
  const reasons: EvictionReason[] = []
  for (const entry of entries) {
    const ci = items.find((it) => it.product_uuid === entry.product_uuid)
    if (!ci) continue
    names.push(ci.product?.product_name?.trim() || 'An artwork')
    reasons.push(entry.reason === 'disabled' ? 'disabled' : 'sold')
    if (!contactUuid) continue
    try {
      await removeFromCart({
        id: ci.id,
        uuid: ci.uuid,
        cart_uuid: ci.cart_uuid,
        contact_uuid: contactUuid,
      })
    } catch {
      // Best-effort delete. The payment-step capture gate is still in
      // play and the next checkout-page mount will retry eviction.
    }
  }

  // Only persist the toast payload when we're navigating away to /cart
  // (stay:false). The /cart page reads it on mount, shows the toast,
  // and clears the storage.
  //
  // For the /cart self-check (stay:true) the caller's `.then` already
  // shows the toast directly via `showSoldEvictionToast`, so a write
  // here would be pure residue — a payload that lingers in
  // sessionStorage and re-fires the mount-time toast on the *next*
  // visit to /cart (after the buyer navigates away and back). That's
  // the "I keep getting the same toast" bug.
  if (!stay && names.length > 0) writeSoldEvictionToast(names, reasons)

  if (!stay && names.length > 0 && typeof window !== 'undefined') {
    if (window.location.pathname !== '/cart') {
      window.location.href = '/cart'
    }
  }

  return { removed: names.length, names, reasons }
}
